import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { TextField, Button, Grid, Typography, MenuItem } from "@mui/material";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom"; // Use navigate for redirection
import MDBox from "components/MDBox";
import { getStudioDetails } from "store/slice/studiosSlice"; // Changed to get studio details
import { deleteStudio } from "store/slice/studiosSlice"; // Changed to delete studio
import { updateStudioDetails } from "store/slice/studiosSlice"; // Changed to update studio details
import { studioActions } from "store/slice/studiosSlice"; // Changed to studioActions

const validationSchema = Yup.object({
  name: Yup.string().max(255, "Name must be 255 characters or less").required("Name is required"),
  timezone: Yup.string().max(50, "Too long").required("Timezone is required"),
  template_body: Yup.string().nullable(),
});

const TIMEZONES = [
  { label: "UTC -12:00", value: "GMT-12" },
  { label: "UTC -11:00", value: "GMT-11" },
  { label: "UTC -10:00", value: "GMT-10" },
  { label: "UTC -09:00", value: "GMT-9" },
  { label: "UTC -08:00", value: "GMT-8" },
  { label: "UTC -07:00", value: "GMT-7" },
  { label: "UTC -06:00", value: "GMT-6" },
  { label: "UTC -05:00", value: "GMT-5" },
  { label: "UTC -04:00", value: "GMT-4" },
  { label: "UTC -03:00", value: "GMT-3" },
  { label: "UTC -02:00", value: "GMT-2" },
  { label: "UTC -01:00", value: "GMT-1" },
  { label: "UTC +00:00", value: "GMT+0" },
  { label: "UTC +01:00", value: "GMT+1" },
  { label: "UTC +02:00", value: "GMT+2" },
  { label: "UTC +03:00", value: "GMT+3" },
  { label: "UTC +04:00 (Armenia)", value: "GMT+4" },
  { label: "UTC +05:00", value: "GMT+5" },
  { label: "UTC +06:00", value: "GMT+6" },
  { label: "UTC +07:00", value: "GMT+7" },
  { label: "UTC +08:00", value: "GMT+8" },
  { label: "UTC +09:00", value: "GMT+9" },
  { label: "UTC +10:00", value: "GMT+10" },
  { label: "UTC +11:00", value: "GMT+11" },
  { label: "UTC +12:00", value: "GMT+12" },
  { label: "UTC +13:00", value: "GMT+13" },
  { label: "UTC +14:00", value: "GMT+14" },
];

function StudiosEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useSelector((store) => store.studios); // Changed to studios store
  const { studioId } = useParams(); // Changed to studioId

  useEffect(() => {
    if (studioId) {
      dispatch(getStudioDetails(studioId)); // Fetch studio details instead of game
    }
  }, [studioId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(studioActions.reset()); // Reset studio actions
    }
  }, []);

  const details = store.studioDetails; // Changed to studioDetails
  const error = store.error;
  const success = store.success;

  useEffect(() => {
    if (success?.status && studioId == 'new') {
      navigate(`/studios/${details.id}`); // Navigate to studio details page
    }
  }, [success?.status]);

  const initialValues = {
    name: details?.name || "",
    timezone: details?.timezone || "GMT+0",
    template_body: details?.template_body || "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    await dispatch(updateStudioDetails({ studioId, updatedData: values })); // Update studio details
    setSubmitting(false);
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this studio?");
    if (confirmDelete) {
      await dispatch(deleteStudio(studioId)); // Delete the studio
      navigate("/studios"); // Navigate to studios list
    }
  };

  return (
    <DashboardLayout>
      <Typography variant="h4" gutterBottom>
        {studioId == 'new' ? 'Add ': 'Edit'} Studio Details
      </Typography>

      <MDBox mb={5}>
        {success.status || error.status ? (
          <div
            style={{
              color: success.status ? "green" : "red",
              padding: "10px",
              backgroundColor: success.status ? "#d4edda" : "#f8d7da",
              borderRadius: "5px",
              border: `1px solid ${success.status ? "#c3e6cb" : "#f5c6cb"}`,
              marginBottom: "10px",
            }}
          >
            {success.status
              ? "Studio details updated successfully!"
              : "Failed to update studio details. Please try again."}
          </div>
        ) : (
          ""
        )}
      </MDBox>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleChange, handleBlur, touched, errors, isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Studio Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label="Select Timezone"
                  name="timezone"
                  value={values.timezone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.timezone && Boolean(errors.timezone)}
                  helperText={touched.timezone && errors.timezone}
                  variant="outlined"
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                >
                  {TIMEZONES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid container spacing={3}>
             
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <MDBox mb={2}>
                      <Typography variant="h6" fontWeight="medium">
                        Message Template Configuration
                      </Typography>
                      <Typography variant="button" color="text" fontWeight="regular">
                        Enter the message structure below. Use <b>{"{{key}}"}</b> placeholders.
                      </Typography>
                    </MDBox>

                    <TextField
                      fullWidth
                      multiline
                      rows={14} // Մի փոքր ավելացրու rows-ը, որ աջ կողմի հետ համապատասխանի
                      variant="outlined"
                      name="template_body"
                      value={values.template_body}
                      onChange={handleChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f8f9fa",
                          fontFamily: "monospace",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDBox
                      p={2}
                      mt={8} // Մի փոքր իջեցրու, որ հավասարվի տեքստային դաշտի հետ
                      borderRadius="lg"
                      sx={{ border: "1px solid #e0e0e0", backgroundColor: "#ffffff" }}
                    >
                      <Typography variant="h6" fontWeight="bold" mb={2}>
                        Available Variables
                      </Typography>
                      
                      <Grid container spacing={1}>
                        {[
                          { key: "{{table}}", desc: "Table name" },
                          { key: "{{date}}", desc: "Date" },
                          { key: "{{time}}", desc: "Time" },
                          { key: "{{shiftPm}}", desc: "Manager" },
                          { key: "{{gameId}}", desc: "Game ID" },
                          { key: "{{nickname}}", desc: "Nickname" },
                          { key: "{{note}}", desc: "Note" },
                          { key: "{{mistake}}", desc: "Mistake" },
                          { key: "{{resolution}}", desc: "Resolution" },
                        ].map((item) => (
                          <Grid item xs={6} key={item.key}> 
                            <MDBox mb={1}>
                              <Typography
                                variant="caption"
                                fontWeight="bold"
                                color="info"
                                display="inline-block"
                                sx={{
                                  fontFamily: "monospace",
                                  backgroundColor: "#e3f2fd",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  fontSize: "0.7rem"
                                }}
                              >
                                {item.key}
                              </Typography>
                              <Typography variant="caption" display="block" color="text" sx={{ fontSize: "0.65rem", mt: 0.3 }}>
                                {item.desc}
                              </Typography>
                            </MDBox>
                          </Grid>
                        ))}
                      </Grid>
                    </MDBox>
                  </Grid>
                </Grid>

              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>

                {/* Delete Button */}
                {studioId !== 'new' ? (
                  <Button
                    variant="text"
                    color="error"
                    style={{ marginLeft: "10px" }}
                    onClick={handleDelete}
                  >
                    Delete Studio
                  </Button>
                ) : (
                  ""
                )}
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </DashboardLayout>
  );
}

export default StudiosEdit;
